import * as Api from '@ViewModels';
import FacebookLogoUrl from '../../web/assets/2021_Facebook_icon.svg';
import InstagramLogoUrl from '../../web/assets/Instagram_logo_2022.svg';
import LinkedInLogoUrl from '../../web/assets/LinkedIn_icon.svg';
import { SocialMediaPostStatusDetails } from '../index';

/**
 * Gets social media platform logo URL
 * @source Original location: UiUtils.ts.backup line 315
 */
export const getSocialMediaPlatformLogoUrl = (platform?: Api.SocialMediaType) => {
	let platformLogoUrl = '';

	switch (platform) {
		case Api.SocialMediaType.Facebook:
			platformLogoUrl = FacebookLogoUrl;
			break;
		case Api.SocialMediaType.LinkedIn:
			platformLogoUrl = LinkedInLogoUrl;
			break;
		case Api.SocialMediaType.Instagram:
			platformLogoUrl = InstagramLogoUrl;
			break;
		default:
			break;
	}

	return platformLogoUrl;
};

/**
 * Checks social citation
 * @source Original location: UiUtils.ts.backup line 2259
 */
export const checkSocialCitation = (post: Api.ISocialMediaPost | Api.SocialMediaPostViewModel | Api.ITemplate) => {
	return post?.citation != null && post.citation.length > 0;
};

/**
 * Gets status group options
 * @source Original location: UiUtils.ts.backup line 2444
 */
export const getStatusGroupOptions = (post: Api.SocialMediaPostViewModel) => {
	if (post && [Api.PostStatus.Cancelled, Api.PostStatus.Failed, Api.PostStatus.Unknown].includes(post.status)) {
		return SocialMediaPostStatusDetails.CancelledOrFailed;
	} else if (!post || post.status === Api.PostStatus.Draft) {
		return SocialMediaPostStatusDetails.NewOrDraft;
	} else {
		return SocialMediaPostStatusDetails.NotCancelledFailedDraftOrUnknown;
	}
};

/**
 * Checks if post is scheduled, pending, draft, or new
 * @source Original location: UiUtils.ts.backup line 2454
 */
export const isScheduledOrPendingOrDraftOrNew = (
	post: Api.SocialMediaPostViewModel | Api.SocialMediaPostReportViewModel
) => {
	return !post ||
		post?.status === Api.PostStatus.Scheduled ||
		post?.status === Api.PostStatus.Pending ||
		post?.status === Api.PostStatus.Draft
		? true
		: false;
};

/**
 * Maps social media types to post targets
 * @source Original location: UiUtils.ts.backup line 2465
 */
export const mapSocialMediaTypesToPostTargets = (providerTypes: Api.SocialMediaType[]) => {
	const targets: Api.IPostTarget[] = providerTypes?.map(x => {
		return { provider: x };
	});
	return targets;
};

export const getProviderTypesFromPostTargest = (targets: Api.IPostTarget[]) => {
	const types: Api.SocialMediaType[] = targets?.map(x => {
		return x.provider;
	});
	return types;
};

export const sortPlatformTargets = (platforms: Api.IPostTarget[]) => {
	const sortedPlatforms = platforms
		?.sort((u, v) =>
			u?.state?.lastConnectionState?.postTargetDisplayName === v?.state?.lastConnectionState?.postTargetDisplayName
				? 0
				: u?.state?.lastConnectionState?.postTargetDisplayName > v?.state?.lastConnectionState?.postTargetDisplayName
					? 1
					: -1
		)
		?.sort((c, d) =>
			c?.state?.lastConnectionState?.state === d?.state?.lastConnectionState?.state
				? 0
				: c?.state?.lastConnectionState?.state > d?.state?.lastConnectionState?.state
					? 1
					: -1
		)
		?.sort((a, b) => (a.provider === b.provider ? 0 : a.provider > b.provider ? 1 : -1));
	return sortedPlatforms || [];
};

// A page target must match the same page. LinkedIn shares one userId across a member's profile and
// pages, so only fall back to userId for page-less (profile) targets — matching a page on userId
// would let an orphaned profile drag down a still-live page for the same user.
export const targetMatchesConnection = (target: Api.IPostTarget, connection: Api.ISocialMediaConnection): boolean => {
	if (!target || !connection || target.provider !== connection.type) {
		return false;
	}
	if (target.pageId) {
		return target.pageId === connection.postTargetId;
	}
	return !!target.userId && target.userId === connection.userId;
};

// The account behind an orphaned connection is gone, so nothing can be posted to it and it can't be
// reconnected: the editor stands one in as an Unknown-state placeholder, and a post whose last
// attempt outlived the account carries a last connection state that never resolved a name. A
// connected account is never orphaned, however its name happens to resolve.
export const isOrphanedConnection = (connection?: Api.ISocialMediaConnection): boolean => {
	if (!connection) {
		return false;
	}
	if (connection.state === Api.SocialMediaConnectionState.Unknown) {
		return true;
	}
	return connection.userName === 'Unknown' && connection.state !== Api.SocialMediaConnectionState.Connected;
};

export interface IPostTargetIdentity {
	pageId: string | null;
	provider: Api.SocialMediaType;
	userId: string | null;
}

// The server prunes a target by the full identity it stored, so send the post's own target when we
// still hold it and only fall back to the connection once the local prune has dropped it.
export const getPostTargetIdentity = (
	connection: Api.ISocialMediaConnection,
	targets?: Api.IPostTarget[]
): IPostTargetIdentity | null => {
	if (!connection) {
		return null;
	}
	const target = targets?.find(x => targetMatchesConnection(x, connection));
	return target
		? { pageId: target.pageId ?? null, provider: target.provider, userId: target.userId ?? null }
		: { pageId: connection.postTargetId ?? null, provider: connection.type, userId: connection.userId ?? null };
};

// Drops targets whose social account no longer exists (surfaced by the reconciliation as an
// Unknown-state placeholder connection). Those render no selectable row, so a user can't deselect
// them and a reschedule would re-send them — the post then partially fails on send.
export const removeOrphanedTargets = (
	targets: Api.IPostTarget[],
	connections: Api.ISocialMediaConnection[]
): Api.IPostTarget[] => {
	if (!targets?.length) {
		return targets ?? [];
	}
	const orphanedConnections = connections?.filter(x => isOrphanedConnection(x)) ?? [];
	if (!orphanedConnections.length) {
		return targets;
	}
	return targets.filter(target => !orphanedConnections.some(connection => targetMatchesConnection(target, connection)));
};

export const getRatingIntFromText = (ratingText: string) => {
	const numberMap = {
		ONE: 1,
		TWO: 2,
		THREE: 3,
		FOUR: 4,
		FIVE: 5,
	};
	return numberMap[ratingText as keyof typeof numberMap];
};

export function getContentLength(targetChoice: Api.SocialMediaType) {
	if (targetChoice === Api.SocialMediaType.Instagram) {
		return Api.SocialMediaPreviewLimits.Instagram;
	} else if (targetChoice === Api.SocialMediaType.LinkedIn) {
		return Api.SocialMediaPreviewLimits.LinkedIn;
	}
	return Api.SocialMediaPreviewLimits.FaceBook;
}

/**
 * Joins a post body with its signature the way the server does before publishing: one line break after
 * the body as written, then the caption trimmed as a whole. Truncating this string then cuts a preview
 * off where the platform will.
 */
export const joinContentWithSignature = (postContent?: string, signatureContent?: string) => {
	const signature = signatureContent?.trim();
	if (!signature || !postContent?.trim()) {
		return postContent;
	}
	return `${postContent}\n${signature}`.trim();
};

export interface IMentionRange {
	end: number;
	page: Api.IFacebookPageLookupResult;
	start: number;
	text: string;
}

export interface IMentionSegment {
	isMention: boolean;
	page?: Api.IFacebookPageLookupResult;
	text: string;
}

// A Facebook Page mention is stored inline, like a merge field: {{@Page Name|pageId}}.
const MENTION_TOKEN_REGEX = /\{\{@([^{}|]+)\|([^{}|]+)\}\}/g;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const createFacebookMentionToken = (page: Api.IFacebookPageLookupResult) =>
	`{{@${(page.pageName || '').replace(/[{}|]/g, '').trim()}|${page.pageId}}}`;

/** Stored content to what the composer shows, plus the Pages its tokens named. */
export const parseFacebookMentions = (content?: string) => {
	const mentions: Api.IFacebookPageLookupResult[] = [];
	const text = (content || '').replace(MENTION_TOKEN_REGEX, (_, pageName: string, pageId: string) => {
		const page = { pageId: pageId.trim(), pageName: pageName.trim() };
		if (!mentions.some(mention => mention.pageId === page.pageId)) {
			mentions.push(page);
		}
		return `@${page.pageName}`;
	});
	return { content: text, mentions };
};

/** The picked Pages plus any of `more` not already among them, each Page once. */
export const mergeFacebookMentions = (
	picked: readonly Api.IFacebookPageLookupResult[] = [],
	more: readonly Api.IFacebookPageLookupResult[] = []
): Api.IFacebookPageLookupResult[] => {
	const pageIds = new Set(picked.map(mention => mention.pageId));
	const merged = [...picked];
	more.forEach(mention => {
		if (!pageIds.has(mention.pageId)) {
			pageIds.add(mention.pageId);
			merged.push(mention);
		}
	});
	return merged;
};

/** Composer text to stored content: every "@Page Name" of a picked Page becomes its token. */
export const applyFacebookMentions = (content?: string, mentions?: Api.IFacebookPageLookupResult[]) =>
	getMentionSegments(content, getFacebookMentionRanges(content, mentions))
		.map(segment => (segment.isMention ? createFacebookMentionToken(segment.page) : segment.text))
		.join('');

export const getFacebookPageLocationLabel = (location?: Api.IFacebookPageLocation) => {
	const region = location?.state || location?.country;
	return [location?.city, region].filter(Boolean).join(', ');
};

export const getFacebookMentionRanges = (
	content?: string,
	mentions?: readonly Api.IFacebookPageLookupResult[],
	options: { handleBoundary?: boolean; ignoreCase?: boolean } = {}
): IMentionRange[] => {
	const text = content || '';
	const ranges: IMentionRange[] = [];
	// Longest names first, so a Page whose name starts another's is not claimed by the shorter one.
	const pages = (mentions || [])
		.filter(page => !!page?.pageId && !!page.pageName)
		.sort((left, right) => right.pageName.length - left.pageName.length);

	pages.forEach(page => {
		// An Instagram handle runs on through "_" and an inner ".", so a name ends only where a handle would.
		const boundary = options.handleBoundary ? '(?![\\p{L}\\p{N}_]|\\.[\\p{L}\\p{N}_])' : '(?![\\p{L}\\p{N}])';
		const regex = new RegExp(`@${escapeRegExp(page.pageName)}${boundary}`, options.ignoreCase ? 'giu' : 'gu');
		for (const match of text.matchAll(regex)) {
			ranges.push({ end: match.index + match[0].length, page, start: match.index, text: match[0] });
		}
	});

	return ranges;
};

export const getMentionSegments = (content?: string, ranges?: IMentionRange[]): IMentionSegment[] => {
	const text = content || '';
	const claimed: IMentionRange[] = [];
	// Earlier ranges win overlaps.
	(ranges || []).forEach(range => {
		if (claimed.some(existing => range.start < existing.end && range.end > existing.start)) {
			return;
		}

		claimed.push(range);
	});

	const segments: IMentionSegment[] = [];
	let cursor = 0;
	claimed
		.sort((left, right) => left.start - right.start)
		.forEach(range => {
			if (range.start > cursor) {
				segments.push({ isMention: false, text: text.slice(cursor, range.start) });
			}

			segments.push({ isMention: true, page: range.page, text: range.text });
			cursor = range.end;
		});

	if (cursor < text.length || !segments.length) {
		segments.push({ isMention: false, text: text.slice(cursor) });
	}

	return segments;
};

/** Instagram and LinkedIn cannot tag a Page, so they show the name on its own. */
export const stripMentionMarkers = (content?: string, ranges?: IMentionRange[]): string =>
	getMentionSegments(content, ranges)
		.map(segment => (segment.isMention ? segment.text.replace(/^@/, '') : segment.text))
		.join('');

export const INSTAGRAM_CAPTION_LIMITS = {
	hashtags: 30,
} as const;

export const getInstagramCaptionCounts = (content?: string) => ({
	hashtags: (content || '').match(/(^|[^A-Za-z0-9_])#[A-Za-z0-9_]+/g)?.length || 0,
});

// Letters, digits, periods and underscores, at most 30, with no period first, last or doubled.
const INSTAGRAM_HANDLE_PATTERN = /^(?!\.)(?!.*\.\.)(?!.*\.$)[A-Za-z0-9._]{1,30}$/;
// A letter, digit, underscore, @ or slash before the @ makes it part of a word, an email or a URL, not a tag.
const INSTAGRAM_HANDLE_REGEX = /(^|[^\p{L}\p{N}_@/])@([A-Za-z0-9._]+)/gu;

const isInstagramHandle = (text: string) => INSTAGRAM_HANDLE_PATTERN.test(text);

/**
 * Picked Pages whose names cannot be an Instagram handle, such as "Acme Insurance". Instagram's version
 * keeps posting those as the bare name, so a Page mention copied in from the base post never tags
 * whichever account owns its first word.
 */
const getNameOnlyPages = (mentions?: readonly Api.IFacebookPageLookupResult[]) =>
	(mentions || []).filter(page => !!page?.pageName && !isInstagramHandle(page.pageName));

const getInstagramNameOnlyRanges = (content?: string, mentions?: readonly Api.IFacebookPageLookupResult[]) =>
	getFacebookMentionRanges(content, getNameOnlyPages(mentions), { handleBoundary: true, ignoreCase: true });

/** True once an Instagram caption mentions a picked Page that Instagram posts by name. */
export const hasInstagramPageMentions = (content?: string, mentions?: readonly Api.IFacebookPageLookupResult[]) =>
	getInstagramNameOnlyRanges(content, mentions).length > 0;

/**
 * The @handles an Instagram caption tags, lowercased, each once, in the order they first appear. Mentions
 * of the picked Pages Instagram posts by name are not handles.
 */
export const getInstagramHandles = (content?: string, mentions?: readonly Api.IFacebookPageLookupResult[]) => {
	const text = getMentionSegments(content, getInstagramNameOnlyRanges(content, mentions))
		.map(segment => (segment.isMention ? ' ' : segment.text))
		.join('');
	const handles = new Set<string>();
	for (const match of text.matchAll(INSTAGRAM_HANDLE_REGEX)) {
		// A period that ends the sentence is not part of the handle.
		const handle = match[2].replace(/\.+$/, '').toLowerCase();
		if (isInstagramHandle(handle)) {
			handles.add(handle);
		}
	}
	return [...handles];
};

/**
 * Composer text for one platform's stored version. Instagram versions saved before Instagram kept its
 * @handles published each Page token as the bare name, so a handle-shaped one loads as that name: as
 * "@name" it would start tagging whichever account owns the handle on the next save.
 */
export const parsePlatformMentions = (content: string | undefined, platform: Api.SocialMediaType) => {
	if (platform !== Api.SocialMediaType.Instagram) {
		return parseFacebookMentions(content);
	}
	const withBareHandles = (content || '').replace(MENTION_TOKEN_REGEX, (token, pageName: string) =>
		isInstagramHandle(pageName.trim()) ? pageName.trim() : token
	);
	return parseFacebookMentions(withBareHandles);
};

export const getInstagramProfileUrl = (handle: string) => `https://www.instagram.com/${encodeURIComponent(handle)}/`;

/**
 * Stored content for one platform's own version. Instagram tags the @handles in its caption, so its
 * version keeps them as written and turns only the Pages it posts by name into tokens.
 */
export const applyPlatformMentions = (
	content: string | undefined,
	platform: Api.SocialMediaType,
	mentions?: Api.IFacebookPageLookupResult[]
) =>
	platform === Api.SocialMediaType.Instagram
		? getMentionSegments(content, getInstagramNameOnlyRanges(content, mentions))
				.map(segment => (segment.isMention ? createFacebookMentionToken(segment.page) : segment.text))
				.join('')
		: applyFacebookMentions(content, mentions);

/**
 * A new version's text copied from the base post. A picked Page whose name is handle-shaped loses its @ in
 * Instagram's copy: the author picked a Facebook Page, and as "@name" it would tag whichever Instagram
 * account owns that handle.
 */
export const seedPlatformContent = (
	content: string,
	platform: Api.SocialMediaType,
	mentions?: readonly Api.IFacebookPageLookupResult[]
) => {
	if (platform !== Api.SocialMediaType.Instagram) {
		return content;
	}
	// Every picked Page competes for the text, so "@acme" never claims the start of "@acme Insurance".
	return getMentionSegments(content, getFacebookMentionRanges(content, mentions, { handleBoundary: true }))
		.map(segment =>
			segment.isMention && isInstagramHandle(segment.page?.pageName ?? '') ? segment.text.slice(1) : segment.text
		)
		.join('');
};
